import { useCallback, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { Button, CheckboxGroup, InputGroup } from '@/common'
import EmailInputGroup from '@/shared/components/EmailInputGroup'
import { SignUpFormData, signUpFormSchema } from './SignUpForm.schema'

export default function SignUpForm({
  submit,
  invitationCode,
  isSigningUp
}: {
  submit: (payload: SignUpFormData) => void
  invitationCode?: string
  isSigningUp?: boolean
}) {
  const { t } = useTranslation()
  const [formState, setFormState] = useState<SignUpFormData>({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    invitationCode
  })
  const [isLegalTermsAccepted, setIsLegalTermsAccepted] = useState(false)
  const [fieldErrors, setFieldErrors] = useState<{
    firstName?: string
    lastName?: string
    email?: string
    password?: string
    confirmPassword?: string
  }>({})

  const onChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const { name } = evt.target
    setFormState(prev => ({
      ...prev,
      [name]: evt.target.value
    }))
    // Clear error when user starts typing
    if (name in fieldErrors) {
      setFieldErrors(prev => ({ ...prev, [name]: undefined }))
    }
  }

  const validateField = useCallback((fieldName: keyof typeof fieldErrors, value: string) => {
    const fieldSchema = signUpFormSchema.shape[fieldName]

    if (!fieldSchema) {
      return true
    }

    const result = fieldSchema.safeParse(value)
    if (!result.success) {
      setFieldErrors(prev => ({
        ...prev,
        [fieldName]: result.error.issues[0].message
      }))
      return false
    } else {
      setFieldErrors(prev => ({ ...prev, [fieldName]: undefined }))
      return true
    }
  }, [])

  const validateConfirmPassword = useCallback(
    (confirmPassword: string) => {
      if (formState.password !== confirmPassword) {
        setFieldErrors(prev => ({
          ...prev,
          confirmPassword: 'signupForm.fields.confirmPassword.errors.not_match'
        }))
        return false
      }
      setFieldErrors(prev => ({ ...prev, confirmPassword: undefined }))
      return true
    },
    [formState.password]
  )

  const onSubmit = useCallback(
    async (evt: React.FormEvent<HTMLFormElement>) => {
      evt.preventDefault()

      // Validate all fields before submit
      const isFirstNameValid = validateField('firstName', formState.firstName)
      const isLastNameValid = validateField('lastName', formState.lastName)
      const isEmailValid = validateField('email', formState.email)
      const isPasswordValid = validateField('password', formState.password)
      const isConfirmPasswordValid = validateConfirmPassword(formState.confirmPassword)

      if (
        !isFirstNameValid ||
        !isLastNameValid ||
        !isPasswordValid ||
        !isConfirmPasswordValid ||
        !isEmailValid
      ) {
        return // Don't submit if validation fails
      }

      await submit(formState)
    },
    [formState, submit, validateField, validateConfirmPassword]
  )

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      {invitationCode && (
        <InputGroup
          onChange={val =>
            setFormState(prev => ({ ...prev, invitationCode: val.currentTarget.value }))
          }
          name="invitationCode"
          label={t('signupForm.fields.invitationCode.label')}
          value={invitationCode || ''}
        />
      )}

      <div className="flex w-full gap-4">
        <div className="flex-1">
          <InputGroup
            onChange={onChange}
            onBlur={evt => validateField('firstName', evt.target.value)}
            name="firstName"
            label={t('signupForm.fields.firstName.label')}
            error={fieldErrors.firstName ? t(fieldErrors.firstName) : undefined}
          />
        </div>
        <div className="flex-1">
          <InputGroup
            onChange={onChange}
            onBlur={evt => validateField('lastName', evt.target.value)}
            name="lastName"
            label={t('signupForm.fields.lastName.label')}
            error={fieldErrors.lastName ? t(fieldErrors.lastName) : undefined}
          />
        </div>
      </div>

      <EmailInputGroup
        onChange={onChange}
        name="email"
        label={t('signupForm.fields.email.label')}
        error={fieldErrors.email ? t(fieldErrors.email) : undefined}
      />

      <InputGroup
        onChange={onChange}
        onBlur={evt => validateField('password', evt.target.value)}
        name="password"
        type="password"
        label={t('signupForm.fields.password.label')}
        hint={t('signupForm.fields.password.hint')}
        error={fieldErrors.password ? t(fieldErrors.password) : undefined}
      />

      <InputGroup
        onChange={onChange}
        onBlur={evt => validateConfirmPassword(evt.target.value)}
        name="confirmPassword"
        type="password"
        label={t('signupForm.fields.confirmPassword.label')}
        error={fieldErrors.confirmPassword ? t(fieldErrors.confirmPassword) : undefined}
      />

      <div className="flex justify-center">
        <CheckboxGroup
          id="legal-terms"
          className="mr-2 cursor-pointer"
          checked={isLegalTermsAccepted}
          onChange={() => setIsLegalTermsAccepted(!isLegalTermsAccepted)}
          label={
            <Trans
              i18nKey="signupForm.acceptLegalTerms"
              components={{
                TermsLink: (
                  <Link
                    to="/terms-of-service"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline text-green-600 hover:text-green-600"
                  />
                ),
                PrivacyLink: (
                  <Link
                    to="/privacy-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline text-green-600 hover:text-green-600"
                  />
                )
              }}
            />
          }
        />
      </div>

      <Button
        type="submit"
        sizeType="sm"
        className="w-full"
        isLoading={isSigningUp}
        disabled={!isLegalTermsAccepted || isSigningUp}
      >
        {t('signupForm.actions.signup')}
      </Button>
    </form>
  )
}
